import React, {
  useCallback,
  type MouseEvent,
  type AnchorHTMLAttributes,
} from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { type LinkComponent, makeLinkComponent } from 'braid-design-system';
import { filterQueryParams } from 'src/utils/filterQueryParams';
import { isAppUrl } from 'src/utils/isAppUrl';
import { link } from 'src/modules/analyticsTracker';
import type { ClickHandlerWithArgs } from '@seek/branding-assets';

export interface Props extends AnchorHTMLAttributes<HTMLAnchorElement> {
  ['data-analytics']?: string;
  ['data-analytics-origin']?: string;
  ['data-testid']?: string;
}

const onLinkClick =
  (href?: string, onClick?: ClickHandlerWithArgs) =>
  (event: MouseEvent<HTMLAnchorElement>) => {
    const trackingEventName =
      event.currentTarget.getAttribute('data-analytics');
    const trackingActionOrigin =
      event.currentTarget.getAttribute('data-analytics-origin') || '';

    if (href === '#') {
      event.preventDefault();
    }

    if (trackingEventName) {
      link(trackingEventName, { actionOrigin: trackingActionOrigin });
    }

    if (onClick) {
      onClick(event);
      return;
    }

    return;
  };

const TrackingLink: LinkComponent = (props: Props) => {
  const { href, onClick, ...restProps } = props;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onTrackingLinkClick = useCallback(onLinkClick(href, onClick), [
    href,
    onClick,
  ]);

  if (!href) {
    return null;
  }

  const queryParams =
    typeof window !== 'undefined'
      ? filterQueryParams(window.location.search)
      : undefined;
  const hrefWithOptionalParams = queryParams ? `${href}?${queryParams}` : href;

  return isAppUrl(hrefWithOptionalParams) ? (
    <ReactRouterLink
      {...(restProps as any)}
      to={hrefWithOptionalParams}
      onClick={onTrackingLinkClick}
    />
  ) : (
    <a
      {...restProps}
      href={hrefWithOptionalParams}
      onClick={onTrackingLinkClick}
    />
  );
};

export default makeLinkComponent((props) => <TrackingLink {...props} />);

export const linkRenderer =
  (actionOrigin: string) =>
  ({ href, ...rendererProps }: Props) =>
    (
      <a
        href={href}
        {...rendererProps}
        data-analytics-origin={actionOrigin}
        onClick={onLinkClick(href)}
      />
    );

export { TrackingLink as TrackingLinkForTest };
